import React, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styles from './ExerciseGroup.module.scss';
import { locales, DragTypes, indexToAlphabetMap } from '../../constants';
import GroupSet from './GroupSet';
import OpenWithOutlinedIcon from '@mui/icons-material/OpenWithOutlined';
import DropZone from './DropZone';

import type { Exercise, ExerciseGroup, SetGroup } from '../../types';

const texts = locales.en.components.workouts.exerciseGroup;

export interface ExerciseGroupProps {
  trainerMergedExercises: Record<string, Exercise>;
  exerciseGroup: ExerciseGroup;
  updateExerciseGroup: (exerciseGroup: ExerciseGroup) => void;
  moveExerciseGroup: (
    groupOriginIndex: number,
    groupTargetIndex: number,
  ) => void;
  onSetGroupMoveToExistingExerciseGroup: (
    draggedExerciseGroupIndex: number,
    draggedSetGroupIndex: number,
    setGroup: SetGroup,
    targetSetIndex?: number,
  ) => void;
  onUnifyExerciseGroups: (
    draggedExerciseGroup: ExerciseGroup,
    draggedExerciseGroupIndex: number,
    targetExerciseGroupIndex: number,
    targetSetIndex?: number,
  ) => void;
  onExerciseDrop: (
    exercise: Exercise,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => void;
  onSetGroupDropFromExerciseGroup: (
    exercise: Exercise,
    exerciseGroupIndexToRemove: number,
    groupIndexToRemove: number,
    newExerciseGroupIndex: number,
    setGroup: SetGroup,
    trainerNote?: string,
  ) => void;
  onDropExerciseAfterExerciseGroup: (
    exercise: Exercise,
    indexToInsertExerciseGroup: number,
  ) => void;
  exerciseGroupIndex: number;
  onExerciseDropAtPosition: (
    exercise: Exercise,
    targetIndex: number,
    exerciseGroupIndexToRemove?: number,
    groupIndexToRemove?: number,
  ) => void;
}

const ExerciseGroupComponent: React.FC<ExerciseGroupProps> = ({
  exerciseGroup,
  trainerMergedExercises,
  moveExerciseGroup,
  onSetGroupMoveToExistingExerciseGroup,
  onDropExerciseAfterExerciseGroup,
  onUnifyExerciseGroups,
  onExerciseDrop,
  onSetGroupDropFromExerciseGroup,
  updateExerciseGroup,
  exerciseGroupIndex,
  onExerciseDropAtPosition,
}) => {
  const [dropZoneVisible, setDropZoneVisible] = useState(false);
  const draggedExerciseGroupIndexRef = React.useRef<number | null>(null);
  const [childDropActive, setChildDropActive] = useState(false);
  const [activeDragSource, setActiveDragSource] = useState<{
    exerciseGroupIndex: number;
    groupSetIndex: number;
  } | null>(null);

  useEffect(() => {
    setActiveDragSource(null);
  }, [exerciseGroup.sets.length]);

  useEffect(() => {
    const resetState = () => {
      setActiveDragSource(null);
      setDropZoneVisible(false);
      setChildDropActive(false);
    };
    window.addEventListener('globalDragEnd', resetState);
    window.addEventListener('groupSetDropped', resetState);
    return () => {
      window.removeEventListener('globalDragEnd', resetState);
      window.removeEventListener('groupSetDropped', resetState);
    };
  }, []);

  const handleDropZoneEnter = useCallback(() => setChildDropActive(true), []);
  const handleDropZoneLeave = useCallback(() => setChildDropActive(false), []);
  const exerciseGroupRef = useRef<HTMLDivElement>(null);
  const updateSets = (sets: SetGroup, groupSetsIndex: number) => {
    const updatedSets = exerciseGroup.sets
      .map((setGroup, index) => (index === groupSetsIndex ? sets : setGroup))
      .filter((sets) => sets.length > 0);
    updateExerciseGroup({ ...exerciseGroup, sets: updatedSets });
  };

  const updateTrainerNote = (groupSetIndex: number, note: string) => {
    const updatedTrainerNotes = [...(exerciseGroup.trainerNotes || [])];
    updatedTrainerNotes[groupSetIndex] = note;
    updateExerciseGroup({
      ...exerciseGroup,
      trainerNotes: updatedTrainerNotes,
    });
  };

  const onReorderGroupSet = (draggedIndex: number, targetIndex: number) => {
    let adjustedTarget = targetIndex;
    if (draggedIndex < targetIndex) {
      adjustedTarget -= 1;
    }
    const newSets = [...exerciseGroup.sets];
    const [movedSet] = newSets.splice(draggedIndex, 1);
    newSets.splice(adjustedTarget, 0, movedSet);
    let newTrainerNotes = exerciseGroup.trainerNotes
      ? [...exerciseGroup.trainerNotes]
      : [];
    if (newTrainerNotes.length < newSets.length) {
      newTrainerNotes = [
        ...newTrainerNotes,
        ...Array(newSets.length - newTrainerNotes.length).fill(''),
      ];
    }
    const [movedNote] = newTrainerNotes.splice(draggedIndex, 1);
    newTrainerNotes.splice(adjustedTarget, 0, movedNote);
    updateExerciseGroup({
      ...exerciseGroup,
      sets: newSets,
      trainerNotes: newTrainerNotes,
    });
  };

  const handleExerciseGroupDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: DragTypes.ExerciseGroupReorder,
        exerciseGroupIndex,
        groupSetIndex: 0,
        exerciseGroup: exerciseGroup,
      }),
    );
    draggedExerciseGroupIndexRef.current = exerciseGroupIndex;
  };

  const handleDropAfterExerciseGroup = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const data = e.dataTransfer.getData('application/json');
    const parsedData = JSON.parse(data);
    const droppedExerciseGroupIndex = parsedData.exerciseGroupIndex;
    switch (parsedData.type) {
      case DragTypes.GroupSetReorder:
        const { exercise, groupSetIndex, setGroup } = parsedData;
        const newExerciseGroupIndex = exerciseGroupIndex + 1;
        let trainerNote;
        if (
          typeof droppedExerciseGroupIndex === 'number' &&
          Array.isArray(exerciseGroup.trainerNotes)
        ) {
          const sourceGroup =
            droppedExerciseGroupIndex === exerciseGroupIndex
              ? exerciseGroup
              : undefined;
          if (sourceGroup && sourceGroup.trainerNotes) {
            trainerNote = sourceGroup.trainerNotes[groupSetIndex];
          }
        }
        onSetGroupDropFromExerciseGroup(
          exercise,
          droppedExerciseGroupIndex,
          groupSetIndex,
          newExerciseGroupIndex,
          setGroup,
          trainerNote,
        );
        break;
      case DragTypes.ExerciseGroupReorder:
        if (droppedExerciseGroupIndex !== exerciseGroupIndex) {
          moveExerciseGroup(droppedExerciseGroupIndex, exerciseGroupIndex + 1);
        }
        break;
      case DragTypes.DragExercise:
        onDropExerciseAfterExerciseGroup(
          parsedData.exercise,
          exerciseGroupIndex + 1,
        );
        break;
    }
    draggedExerciseGroupIndexRef.current = null;
    setDropZoneVisible(false);
  };

  const renderExerciseGroupHeader = () => {
    const numSets = exerciseGroup.sets.length;
    let groupLabel = texts.exerciseGroup;
    if (numSets === 2) {
      groupLabel = texts.superset;
    } else if (numSets >= 3) {
      groupLabel = texts.circuit;
    }
    return (
      <div className={styles.headerWrapper}>
        <div
          className={styles.exerciseGroupTitle}
        >{`${groupLabel} ${indexToAlphabetMap[exerciseGroupIndex] || 'Extra'}`}</div>
        <div
          className={styles.exerciseGroupDragHandle}
          draggable
          onDragStart={handleExerciseGroupDragStart}
          onDragEnd={handleDragEnd}
        >
          <OpenWithOutlinedIcon style={{ color: 'var(--color-text-3)' }} />
        </div>
      </div>
    );
  };

  const circuit = exerciseGroup.sets;
  const isCircuit = circuit.length > 1;

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show when entering the ExerciseGroup container itself, not its children
    if (e.currentTarget === e.target ||
      (e.target as HTMLElement).closest(`.${styles.exerciseGroup}`) === exerciseGroupRef.current) {
      setDropZoneVisible(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneVisible(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as Node | null;
    if (!exerciseGroupRef.current?.contains(relatedTarget)) {
      setDropZoneVisible(false);
    }
  };

  const handleDragEnd = () => {
    draggedExerciseGroupIndexRef.current = null;
  };

  return (
    <div
      className={styles.exerciseGroup}
      onDragOver={handleDragOver}
      ref={exerciseGroupRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDropAfterExerciseGroup}
    >
      {renderExerciseGroupHeader()}
      <div className={styles.exerciseGroupContent}>
        <div className={styles.groupSetsWrapper}>
          {circuit.map((setGroup, index) => (
            <GroupSet
              exerciseGroup={exerciseGroup}
              exerciseGroupIndex={exerciseGroupIndex}
              onReorderGroupSet={onReorderGroupSet}
              key={`groupSet-${exerciseGroup.name}-${index}`}
              groupSetIndex={index}
              updateSets={(newSets) => updateSets(newSets, index)}
              onSetGroupMoveToExistingExerciseGroup={
                onSetGroupMoveToExistingExerciseGroup
              }
              onUnifyExerciseGroups={onUnifyExerciseGroups}
              onExerciseDrop={onExerciseDrop}
              exercises={trainerMergedExercises}
              setGroup={setGroup}
              enableInternalDrag={isCircuit}
              updateTrainerNote={updateTrainerNote}
              totalGroupSets={circuit.length}
              onExerciseDropAtPosition={onExerciseDropAtPosition}
              onGroupSetDragStart={(gIdx) => {
                setActiveDragSource({ exerciseGroupIndex, groupSetIndex: gIdx });
                draggedExerciseGroupIndexRef.current = exerciseGroupIndex;
              }}
              onGroupSetDragEnd={() => {
                setActiveDragSource(null);
                draggedExerciseGroupIndexRef.current = null;
              }}
              activeDragSource={activeDragSource}
              onDropZoneEnter={handleDropZoneEnter}
              onDropZoneLeave={handleDropZoneLeave}
            />
          ))}
        </div>
      </div>
      <DropZone
        className={classNames(styles.dropZone, {
          [styles.dropZoneVisible]: dropZoneVisible && !childDropActive,
        })}
        onDrop={handleDropAfterExerciseGroup}
        text={texts.dropToCreateGroup}
      />
    </div>
  );
};

export default ExerciseGroupComponent;
